import React, { Component } from 'react';
import { connect } from 'react-redux';
import { TweenMax, TimelineLite } from 'gsap';
import PropTypes from 'prop-types';

import DragDrop from 'components/Draggable/DragDrop';
import InactivityManager from 'utils/InactivityManager';
import { showVisualCue, hideVisualCue } from 'store/actions/ui';
import { getElementId } from 'utils/accessibilityUtils.js';

import style from './PuzzlePieces.module.scss';
import { t } from '../translations';

const getOrder = length =>
  length === 3 ? { left: 0, center: 1, right: 2 } : { left: 0, right: 1 };

const TIMEOUTS = {
  makePuzzle: 10 * 1000,
  solvePuzzle: 20 * 1000
};

const REASONS = {
  didNotMakePuzzle: 'Did not make puzzle',
  didNotSolvePuzzle: 'Did not solve puzzle'
};

const ERRORS = {
  ERROR_TYPES: {
    NotAMatch: 'NotAMatch',
    MissedTarget: 'MissedTarget'
  },
  COMPETENCY: {
    Matching: 'Matching',
    DragAndDrop: 'DragAndDrop'
  }
};

const PuzzleImage = ({ puzzlePiece, isMatched }) => (
  // location
  <figure className={style.figure} id={`target-${puzzlePiece.id}`}>
    <svg width="100%" height="100%">
      <title>
        {isMatched[puzzlePiece.id]
          ? puzzlePiece.image.description
          : puzzlePiece.puzzlePiece?.missing?.description}
      </title>
      <image
        className="mainImg"
        xlinkHref={puzzlePiece.image.src}
        width="100%"
        height="100%"
        alt={puzzlePiece.image.description}
      />
      {!isMatched[puzzlePiece.id] && (
        <image
          xlinkHref={puzzlePiece.puzzlePiece.missing.src}
          width="100%"
          height="100%"
          alt={puzzlePiece.puzzlePiece?.missing?.description}
        />
      )}
    </svg>
  </figure>
);

const PuzzlePiece = ({ puzzlePiece, isMatched }) => (
  // position
  <figure id={`drag-${puzzlePiece.id}`} className={style.figure}>
    {isMatched[puzzlePiece.id] ? null : (
      <svg width="100%" height="100%">
        <title>
          {puzzlePiece.puzzlePiece.piece.description + ' ' + puzzlePiece.image.description}
        </title>
        <mask id={`mask-${puzzlePiece.id}`}>
          <image xlinkHref={puzzlePiece.puzzlePiece.piece.src} width="100%" height="100%" />
        </mask>
        <image
          xlinkHref={puzzlePiece.puzzlePiece.piece.src}
          width="100%"
          height="100%"
          alt={puzzlePiece.puzzlePiece.piece.description + ' ' + puzzlePiece.image.description}
        />
        <svg width="100%" mask={`url(#mask-${puzzlePiece.id})`}>
          <image xlinkHref={puzzlePiece.image.src} width="100%" height="100%" />
        </svg>
      </svg>
    )}
  </figure>
);

class PuzzlePieces extends Component {
  constructor(props) {
    super(props);

    const {
      currentRound: {
        data: {
          puzzlePieces: rawPuzzlePieces,
          instructions: { beginActivity }
        }
      }
    } = props;

    const puzzlePieces = rawPuzzlePieces.map(object => ({
      ...object,
      id: object.puzzlePiece.piece.id
    }));
    // init as true so the pieces don't show up yet
    const matched = puzzlePieces.reduce((acc, curr) => ({ ...acc, [curr.id]: true }), {});
    const sortedPieces = [];

    puzzlePieces.forEach((piece, index) => {
      const idx = getOrder(puzzlePieces.length)[piece.location];
      sortedPieces[idx] = {
        ...piece,
        beginActivity: beginActivity[index].src
      };
    });

    const targets = sortedPieces.map(({ id }) => ({
      id,
      correct: true,
      ref: React.createRef()
    }));

    this.state = {
      matched,
      targets,
      matchCount: 0,
      gameCanStart: false,
      gameStarted: false,
      makingPuzzle: false,
      selectedPuzzle: null,
      feedback: ''
    };

    this.timers = [];
    this.sortedPieces = sortedPieces;
    this.correspondingPairs = [];
    this.inactivityManager = null;
  }

  componentDidMount() {
    this.initPuzzlePieces();
    this.initEventListeners();
    this.puzzleContainer = React.createRef();
  }

  componentDidUpdate(prevProps, prevState) {
    const { matchCount: prevCount } = prevState;
    const {
      props: {
        currentRound: {
          data: { puzzlePieces }
        }
      },
      state: { matchCount: currCount }
    } = this;

    const noOfPieces = puzzlePieces.length;

    if (prevCount < noOfPieces && currCount === noOfPieces) {
      this.inactivityManager.stopAudio();
      this.inactivityManager.clear();
      this.timers.push(setTimeout(this.props.onRoundEnd, 1000));
    }
  }

  componentWillUnmount() {
    TweenMax.killAll();
    this.props.currentRound.stopAllSounds();
    this.timers.length && this.timers.forEach(tID => clearTimeout(tID));
    this.inactivityManager && this.inactivityManager.stopAudio();
    this.inactivityManager && this.inactivityManager.clear();
  }
  addVisualCue(message, interval = 1000) {
    message === 'success'
      ? this.setState({ feedback: t('GoodJob') })
      : this.setState({ feedback: t('TryAgain') });

    this.props.showVisualCue(message);
    this.timers.push(
      setTimeout(() => {
        this.props.hideVisualCue();
        this.setState({ feedback: '' });
      }, interval)
    );
  }

  droppedPuzzle = collision => {
    const {
      recordError,
      state: { matched, targets, matchCount },
      props: {
        commonSounds: {
          common: { wrong: sndWrong }
        }
      }
    } = this;

    const { id: puzzlePieceId, targetId, instance: draggableObject } = collision;

    this.inactivityManager.clear();
    this.inactivityManager.stopAudio();
    this.inactivityManager.watch();

    if (puzzlePieceId === targetId) {
      this.addVisualCue('success');
      this.playPuzzleSound(puzzlePieceId);
      this.wigglePiece(puzzlePieceId);

      const newTargets = [...targets];
      const foundTarget = newTargets.find(target => target.correct && target.id === targetId);

      if (foundTarget) {
        foundTarget.correct = false;

        this.setState({
          matched: { ...matched, [targetId]: true },
          targets: newTargets,
          matchCount: matchCount + 1,
          selectedPuzzle: null
        });
        foundTarget.ref.current.blur();
      }
      this.state.selectedPuzzle?.blur();
    } else {
      this.addVisualCue('error');
      sndWrong && sndWrong.play();
      this.timers.push(setTimeout(() => draggableObject && draggableObject.resetPosition(), 1000));
      recordError(ERRORS.ERROR_TYPES.NotAMatch, { puzzlePieceId, targetId });
    }
  };

  onBadDrag = (x, y, id) =>
    this.recordError(ERRORS.ERROR_TYPES.MissedTarget, { puzzlePieceId: id }, { x, y });

  recordError = (errorType, { puzzlePieceId, targetId }, { ...coords } = false) => {
    const {
      props: {
        logError,
        currentRound: {
          data: { puzzlePieces }
        }
      }
    } = this;

    const getIdOfPuzzlePiece = ({ puzzlePiece }) => puzzlePiece.piece.id;

    const puzzlePiece = puzzlePieces.find(piece => getIdOfPuzzlePiece(piece) === puzzlePieceId);
    const puzzleTarget = targetId
      ? puzzlePieces.find(piece => getIdOfPuzzlePiece(piece) === targetId)
      : null;

    const timestamp = new Date().toISOString();

    const error = {
      error: errorType,
      competency:
        errorType === ERRORS.ERROR_TYPES.NotAMatch
          ? ERRORS.COMPETENCY.Matching
          : ERRORS.COMPETENCY.DragAndDrop,
      timestamp,
      puzzlePiece: {
        id: puzzlePieceId,
        piece: getIdOfPuzzlePiece(puzzlePiece),
        position: puzzlePiece.position
      },
      expected: {
        location: puzzlePiece.location
      },
      actual: {
        ...(targetId
          ? {
              puzzlePiece: {
                id: targetId,
                piece: getIdOfPuzzlePiece(puzzleTarget)
              },
              location: puzzleTarget.location
            }
          : { x: coords.x, y: coords.y })
      }
    };

    logError(error);
  };

  playPuzzleIntroSound = puzzlePieceId => {
    const {
      props: {
        currentRound: { audio }
      },
      sortedPieces
    } = this;

    const id = parseInt(puzzlePieceId, 10);

    return new Promise(resolve => {
      const currentPuzzlePiece = sortedPieces.find(piece => piece.id === id);

      const sound = audio[currentPuzzlePiece.beginActivity] || null;

      if (sound) {
        sound.play();
        sound.once('end', () => this.timers.push(setTimeout(resolve, 200)));
      } else this.timers.push(setTimeout(resolve, 200));
    });
  };

  playPuzzleSound = puzzlePieceId => {
    const {
      props: {
        currentRound: { audio }
      },
      sortedPieces
    } = this;

    const id = parseInt(puzzlePieceId, 10);
    const currentPuzzlePiece = sortedPieces.find(piece => piece.id === id);

    const sound = audio[currentPuzzlePiece.audio.src] || null;

    if (sound) sound.play();
  };

  wigglePiece = puzzlePieceId => {
    const target = document.getElementById(`target-${puzzlePieceId}`);

    if (!target) return;

    TweenMax.fromTo(
      target,
      0.15,
      { x: -10 },
      {
        x: 10,
        repeat: 5,
        yoyo: true,
        ease: Sine.easeInOut, //eslint-disable-line
        onComplete: function() {
          TweenMax.to(this.target, 1.5, { x: 0, ease: Elastic.easeOut }); //eslint-disable-line
        }
      }
    );
  };

  initPuzzlePieces = async () => {
    const {
      props: {
        currentRound: {
          audio,
          data: {
            instructions: { beforePuzzle }
          }
        }
      }
    } = this;

    const targets = Array.from(document.querySelectorAll('[id ^= "target-"]')) || null;
    const pieces = Array.from(document.querySelectorAll('[id ^= "drag-"]')) || null;

    if (!pieces || !targets) return;

    this.correspondingPairs = targets.reduce((acc, curr) => {
      const id = curr.id.split('-').pop();
      const target = curr;
      const piece = pieces.find(target => target.id.split('-').pop() === id);

      if (piece) acc.push({ piece, target });
      else console.warn('piece not found!!!'); // eslint-disable-line no-console

      return acc;
    }, []);

    this.correspondingPairs.forEach(({ piece, target }) => {
      const { x: targetX, y: targetY } = target.getBoundingClientRect();
      const { x: pieceX, y: pieceY } = piece.getBoundingClientRect();

      TweenMax.set(piece, { x: targetX - pieceX, y: targetY - pieceY });
    });

    // eslint-disable-next-line no-unused-vars
    for (const pair of this.correspondingPairs) {
      const { target } = pair;

      const id = target.id.split('-').pop();

      TweenMax.fromTo(
        target,
        0.15,
        { x: -10 },
        {
          x: 10,
          repeat: 5,
          yoyo: true,
          ease: Sine.easeInOut, //eslint-disable-line
          onComplete: function() {
            TweenMax.to(this.target, 1.5, { x: 0, ease: Elastic.easeOut }); //eslint-disable-line
          }
        }
      );

      await this.playPuzzleIntroSound(id); //eslint-disable-line
    }

    const sound = audio[beforePuzzle.src] || null;

    if (sound) {
      this.timers.push(setTimeout(() => sound.play(), 200));
      sound.once('end', () => this.setState({ gameCanStart: true }));
    } else this.timers.push(setTimeout(() => this.setState({ gameCanStart: true }), 200));

    this.inactivityManager = new InactivityManager(
      TIMEOUTS.makePuzzle,
      () => (sound ? sound.play() : () => {}),
      REASONS.didNotMakePuzzle
    );

    this.inactivityManager.watch();
  };

  initEventListeners = () => {
    const listenForEnter = async event => {
      if (event.key === 'Enter' && !this.state.gameStarted && this.state.gameCanStart) {
        await this.makePuzzle();
        document.removeEventListener('keydown', listenForEnter);
      }
    };
    document.addEventListener('keydown', listenForEnter);
  };

  animatePuzzlePiece = piece =>
    new Promise(resolve => {
      const {
        state: { matched }
      } = this;
      const id = piece.id.split('-').pop();
      this.setState({ matched: { ...matched, [id]: false } });
      const timeline = new TimelineLite();
      timeline
        .to(piece, 0.4, {
          x: '+=30px',
          y: '+=20px',
          scale: 1.2,
          delay: 0.1
          // ease: Power2.easeOut // eslint-disable-line
        })
        .to(piece, 0.3, {
          bezier: {
            values: [
              { x: '-=100', y: '-=50' },
              { x: '-=100', y: '+=50' },
              { x: '+=50', y: '+=150' }
            ]
          },
          scale: 1.3,
          ease: Power2.easeOut // eslint-disable-line
        })
        .to(piece, 0.2, { x: 0, y: 0, scale: 1, ease: Power2.easeOut }) // eslint-disable-line
        .call(() => this.timers.push(setTimeout(resolve(), 100)));
    });

  makePuzzle = async () => {
    const {
      props: {
        currentRound: {
          audio,
          data: {
            instructions: { afterPuzzle }
          }
        }
      },
      state: { gameCanStart, makingPuzzle }
    } = this;

    if (!gameCanStart || makingPuzzle) return;

    this.setState({ makingPuzzle: true });
    this.inactivityManager.clear();

    // eslint-disable-next-line
    for (const pair of this.correspondingPairs) {
      const { piece } = pair;
      this.timers.push(
        setTimeout(
          () =>
            (piece.nextSibling.textContent =
              piece.parentElement?.getAttribute('aria-label') + t('added')),
          500
        )
      );
      await this.animatePuzzlePiece(piece); // eslint-disable-line
    }

    const sound = audio[afterPuzzle.src] || null;

    if (sound) {
      this.timers.push(setTimeout(() => sound.play(), 200));

      sound.once('end', () =>
        this.setState({
          gameStarted: true,
          makingPuzzle: false
        })
      );
    } else
      this.timers.push(
        setTimeout(
          () =>
            this.setState({
              gameStarted: true,
              makingPuzzle: false
            }),
          200
        )
      );

    this.inactivityManager = new InactivityManager(
      TIMEOUTS.solvePuzzle,
      () => (sound ? sound.play() : () => {}),
      REASONS.didNotSolvePuzzle
    );

    this.inactivityManager.watch();
  };
  handleSelectPuzzle = (ev, isSelected) => {
    const { selectedPuzzle: prevSelectedPuzzle } = this.state;
    if (prevSelectedPuzzle) {
      prevSelectedPuzzle.setAttribute('aria-pressed', false);
      prevSelectedPuzzle.style['border'] = 'none';
    }
    this.setState({ selectedPuzzle: isSelected ? ev.target : null });
  };

  handleDrop = ref => {
    const targetId = getElementId(ref.current);
    const selectedPuzzleTarget = getElementId(this.state.selectedPuzzle);
    const collision = { id: selectedPuzzleTarget, targetId, instance: null };
    this.droppedPuzzle(collision);
  };

  render() {
    const {
      state: { matched, targets, gameStarted, gameCanStart, selectedPuzzle },
      props: {
        currentRound: { data },
        level
      },
      makePuzzle,
      onBadDrag,
      sortedPieces
    } = this;

    return (
      <div className={style.puzzlePiecesMainParent}>
        <div
          id="puzzle-pieces-game"
          className={style.puzzlePiecesMain}
          data-game={gameStarted ? 'started' : gameCanStart ? 'canStart' : 'notStarted'}
        >
          <div
            role="region"
            aria-live="polite"
            style={{
              position: 'absolute',
              zIndex: '-1000',
              top: '50px',
              width: '1px',
              height: '1px',
              overflow: 'hidden'
            }}
          >
            {this.state.feedback}
          </div>
          <div id="puzzle-container" className={style.puzzlesContainer}>
            {[...sortedPieces]
              .sort(
                (a, b) =>
                  getOrder(sortedPieces.length)[a.position] -
                  getOrder(sortedPieces.length)[b.position]
              )
              .map((puzzlePiece, index) => {
                // these computations need to be done here in order to keep the select with tab behaviour clean
                const isMatched = matched[puzzlePiece.id];
                // order = tabIndex for the piece container
                // explanation: if the game has not started, make pieces unselectable;
                // else if the piece is matched, make it unselectable
                // else give it a tabIndex that is 0
                const order = gameStarted ? (isMatched ? -1 : 0) : -1;

                return (
                  <DragDrop.DraggableWithKeyboard
                    activityType={'PuzzlePieces'}
                    allowDrag={gameStarted}
                    allowTap={false}
                    animationTime={500}
                    callback={this.droppedPuzzle}
                    soundFx={{ sndWrong: false, sndCorrect: false, sndMoving: false }}
                    targets={targets}
                    key={`puzzle-${index}`}
                    level={level}
                    id={puzzlePiece.id}
                    divId={`puzzle-${index}`}
                    onBadDrag={onBadDrag}
                    isMatched={matched[puzzlePiece.id]}
                    order={order}
                    distance={10}
                    mainContainerId={'activity-container'}
                    label={puzzlePiece.puzzlePiece?.piece?.description}
                    onSelect={(ev, isSelected) => this.handleSelectPuzzle(ev, isSelected)}
                  >
                    <PuzzlePiece puzzlePiece={puzzlePiece} isMatched={matched} />
                    <div
                      role="region"
                      aria-live="polite"
                      style={{
                        position: 'absolute',
                        zIndex: '-1000',
                        top: '50px',
                        width: '1px',
                        height: '1px',
                        overflow: 'hidden'
                      }}
                    ></div>
                  </DragDrop.DraggableWithKeyboard>
                );
              })}
          </div>
          <div
            ref={this.puzzleContainer}
            tabIndex="0"
            role="button"
            className={style.puzzleContainer}
            id="puzzleContainer"
            onKeyDown={() => this.initEventListeners()}
            onClick={makePuzzle}
            aria-label={!gameStarted ? t(`PuzzledIn`) : t(`PuzzledOff`)}
          >
            <img
              src={data.letter.src}
              alt={data.letter.description}
              className={style.letterImage}
            />

            <div id="cards-container" className={style.cardsContainer}>
              {sortedPieces.map((puzzlePiece, index) => {
                const id = getElementId(targets[index].ref.current);
                const filledSpot = matched[id];
                const ariaLabelDescription = matched[puzzlePiece.id]
                  ? puzzlePiece?.image.description
                  : puzzlePiece?.puzzlePiece?.missing?.description;
                const noPieceSelected = !this.state.selectedPuzzle;
                return (
                  <DragDrop.Target
                    key={`piece-${index}`}
                    forwardedRef={targets[index].ref}
                    divId={`location-${index}`}
                    gameStarted={this.state.gameStarted}
                    outline={
                      selectedPuzzle && !filledSpot && !matched[puzzlePiece.id] ? 'red' : null
                    }
                    onDrop={ref => selectedPuzzle && this.handleDrop(ref)}
                    filledSpot={filledSpot}
                    ariaLabelDescription={ariaLabelDescription}
                    noPieceSelected={noPieceSelected}
                  >
                    <PuzzleImage puzzlePiece={puzzlePiece} isMatched={matched} />
                  </DragDrop.Target>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const shapeOfAudio = PropTypes.shape({
  id: PropTypes.number,
  src: PropTypes.string,
  vtt: PropTypes.string
});

const shapeOfImage = PropTypes.shape({
  id: PropTypes.number,
  src: PropTypes.string,
  description: PropTypes.string
});

const shapeOfPuzzlePieceProp = PropTypes.shape({
  id: PropTypes.number,
  piece: shapeOfImage,
  missing: shapeOfImage
});

const shapeOfPuzzlePiece = PropTypes.shape({
  id: PropTypes.number,
  image: shapeOfImage,
  audio: shapeOfAudio,
  isMatch: PropTypes.bool,
  position: PropTypes.string,
  location: PropTypes.string,
  puzzlePiece: shapeOfPuzzlePieceProp
});

PuzzleImage.propTypes = {
  puzzlePiece: shapeOfPuzzlePiece,
  isMatched: PropTypes.object
};

PuzzlePiece.propTypes = {
  ...PuzzleImage.propTypes
};

PuzzlePieces.propTypes = {
  commonSounds: PropTypes.object,
  currentRound: PropTypes.shape({
    type: PropTypes.string,
    images: PropTypes.array,
    audio: PropTypes.array,
    data: PropTypes.shape({
      instructions: PropTypes.object,
      puzzlePieces: PropTypes.arrayOf(shapeOfPuzzlePiece),
      letter: shapeOfImage
    }),
    stopAllSounds: PropTypes.func
  }),
  level: PropTypes.number,
  logError: PropTypes.func,
  onRoundEnd: PropTypes.func,
  showVisualCue: PropTypes.func,
  hideVisualCue: PropTypes.func
};

const mapDispatchToProps = {
  showVisualCue,
  hideVisualCue
};

export default connect(null, mapDispatchToProps)(PuzzlePieces);
